import gql from 'graphql-tag';
import type { UseQuery, UseMutation } from '../../../types';

export const policiesQuery = gql`
  query pendingPolicies($first: Int, $pending: Boolean) {
    policies(first: $first, pending: $pending) {
      totalCount
      edges {
        contentId
        id
        publishedAt
        slug
        title
        tldr
      }
    }
  }
`;

const policyAgreeMutation = gql`
  mutation PolicyAgreeMutation($slug: String!, $content: Int!) {
    policyAgreement(slug: $slug, content: $content) {
      agreedAt
    }
  }
`;

type Cache = {
  readQuery: (opts: {}) => any;
  writeQuery: (opts: {}) => void;
};

type Policy = {
  contentId: number;
  slug: string;
  title: string;
  publishedAt: string;
  status: string;
  tldr: string;
};

const updatePoliciesCache = (
  cache: Cache,
  policySlug: string,
  variables: {}
) => {
  const { policies } = cache.readQuery({
    query: policiesQuery,
    variables,
  });
  const updated = {
    ...policies,
    edges: policies.edges.filter(
      (policy: Policy) => policy.slug !== policySlug
    ),
    totalCount: policies.totalCount - 1,
  };
  cache.writeQuery({
    data: {
      policies: updated,
    },
    query: policiesQuery,
    variables,
  });
};

type Options = {
  first?: number;
  pending?: boolean;
  useQuery: UseQuery;
  useMutation: UseMutation;
  onMutationError?: (e: any) => void;
};

type AcceptablePolicy = Policy & {
  onAccept: () => void;
};

type Result = {
  policies: Array<AcceptablePolicy>;
  loading: boolean;
};

export const usePolicyData = (options: Options): Result => {
  const variables = {
    first: options.first,
    pending: options.pending,
  };
  const { data, loading, error } = options.useQuery(policiesQuery, {
    variables,
  });
  const [agreeToPolicy] = options.useMutation(policyAgreeMutation, {
    onError: options.onMutationError,
  });

  if (loading) {
    return { policies: [], loading: true };
  }
  // Assume temporary error and render no policies.
  if (error) {
    return { policies: [], loading: false };
  }

  const policies = data?.policies?.edges || [];

  const transformedPolicies = policies.map((policy: Policy) => ({
    ...policy,
    onAccept: () => {
      agreeToPolicy({
        update: (cache: Cache) => {
          updatePoliciesCache(cache, policy.slug, variables);
        },
        variables: { slug: policy.slug, content: policy.contentId },
      });
    },
  }));

  return { policies: transformedPolicies, loading: false };
};
